import { computed, unref, type Ref } from 'vue'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import type { AxiosResponse } from 'axios'

import { filteringByExpirationDate } from '@/utils/hotelOffersUtils'
import { selectedAccommodationId } from '@/layouts/queries'
import { useSessionValues } from '@/composables/useSessionValues'
import apis from '@/services'

// Import shared types
import type {
  HotelOfferBody,
  TranslationSnippet,
  Translation,
  ActiveOffer,
} from '@/types/hotelOffers'

interface SendSuggestionParams {
  feedback: string
  itemId: string
}

interface CreateOfferParams {
  accommodationId: string
  replaceOldOffer: boolean
  body: HotelOfferBody
}

interface RemoveOfferParams {
  accommodationId: string
  feedback: string
}

interface EditOfferParams {
  accommodationId: string
  body: Partial<HotelOfferBody>
}


export function useOffersTemplates() {
  const { isLoading, isError, isFetching, data } = useQuery({
    queryKey: ['HOTemplates', selectedAccommodationId],
    enabled: !!unref(selectedAccommodationId),
    queryFn: () => apis.hotelOffers.fetchTemplates(),
  })

  const categories = computed(() => data.value?.data?.categories)
  const templates = computed(() => data.value?.data?.templates)

  return { isLoading, isError, isFetching, categories, templates }
}

export function useActiveOffer(accommodationId: Ref<string | number | null>) {
  const { isLoading, isError, isFetching, data } = useQuery({
    queryKey: ['HOActiveOffer', accommodationId],
    enabled: !!unref(accommodationId),
    queryFn: ({ queryKey }) => apis.hotelOffers.getActiveOffer({ accommodationId: queryKey[1] as string }),
  })

  const activeOffer = computed(() => {
    const offer = filteringByExpirationDate(data.value?.data?.offers)
    return offer?.length > 0 ? offer[0] : null
  })

  const allOffers = computed(() => data.value?.data?.offers)
  const hasActiveOffer = computed(() => Boolean(activeOffer.value))
  const hasAnyOffers = computed(() => (data.value?.data?.offers?.length ?? 0) > 0)

  return { isLoading, isError, isFetching, hasActiveOffer, activeOffer, hasAnyOffers, allOffers }
}

export function usePastOffers() {
  const { isLoading, isError, isFetching, data } = useQuery({
    queryKey: ['HOPastOffers', selectedAccommodationId],
    enabled: !!unref(selectedAccommodationId),
    queryFn: ({ queryKey }) => apis.hotelOffers.getPastOffers({ accommodationId: queryKey[1] as string }),
  })

  const pastOffers = computed(() => data.value?.data ?? [])

  return { isLoading, isError, isFetching, pastOffers }
}

const getOfferTranslations = async ({
  snippets,
  languageCode
}: {
  snippets: TranslationSnippet[]
  languageCode: string
}): Promise<Translation[]> => {
  const response = await apis.hotelOffers.fetchOffersTranslations({ snippets, languageCode })
  return response?.data?.data?.getTranslations?.translations
}

export function useOfferTranslations({
  snippets,
  languageCode
}: {
  snippets: Ref<TranslationSnippet[]>
  languageCode: Ref<string>
}) {
  const { isLoading, isError, isFetching, data } = useQuery({
    queryKey: ['HOOfferTranslations', snippets, languageCode],
    queryFn: ({ queryKey }) => getOfferTranslations({
      snippets: queryKey[1] as TranslationSnippet[],
      languageCode: queryKey[2] as string
    }),
  })

  return { isLoading, isError, isFetching, data }
}

const getAccommodationOffer = async ({
  accommodationId,
  languageCode
}: {
  accommodationId: string
  languageCode: string
}): Promise<ActiveOffer | null> => {
  const response = await apis.hotelOffers.fetchAccommodationOffer({ accommodationId, languageCode })
  return response?.data?.data?.getAccommodationDetails?.accommodationDetails?.[0]?.businessStudio?.activeOffer ?? null
}

export function useAccommodationOffer({
  accommodationId,
  languageCode
}: {
  accommodationId: Ref<string>
  languageCode: Ref<string>
}) {
  const { isLoading, isError, isFetching, data } = useQuery({
    queryKey: ['HOAccommodationOffer', accommodationId, languageCode],
    queryFn: ({ queryKey }) => getAccommodationOffer({
      accommodationId: queryKey[1] as string,
      languageCode: queryKey[2] as string
    }),
  })

  return { isAccommodationOfferLoading: isLoading, isError, isFetching, accommodationOffer: data }
}

type MutationFunction<T extends (...args: any[]) => any> = (...args: Parameters<T>) => ReturnType<T>

/**
 * Guard mutation to prevent support users from executing mutations
 */
function guardSupportMutation<T extends (...args: any[]) => any>(
  mutate: T
): MutationFunction<T> {
  return (...args: Parameters<T>) => {
    const { userRole } = useSessionValues()
    if (userRole.value === 'support') return
    return mutate(...args)
  }
}

export function useSendSuggestion() {
  const {
    isPending: isSendSuggestionLoading,
    isSuccess: isSendSuggestionSuccess,
    isError: isSendSuggestionError,
    mutate: sendSuggestionMutation,
  } = useMutation({
    mutationFn: ({ feedback, itemId }: SendSuggestionParams): Promise<AxiosResponse> =>
      apis.hotelOffers.sendFeedback({ feedback, itemId }),
  })

  const sendSuggestion = guardSupportMutation(sendSuggestionMutation)

  return {
    isSendSuggestionLoading,
    isSendSuggestionSuccess,
    isSendSuggestionError,
    sendSuggestion,
  }
}

export function useCreateOffer() {
  const queryClient = useQueryClient()

  const {
    isPending: isCreatingOfferLoading,
    isSuccess: isCreatingOfferSuccess,
    isError: isCreatingOfferError,
    mutate: createOfferMutation,
  } = useMutation({
    mutationFn: ({ accommodationId, replaceOldOffer, body }: CreateOfferParams): Promise<AxiosResponse> =>
      apis.hotelOffers.createOffer({ accommodationId, replaceOldOffer, body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['HOActiveOffer'] })
      queryClient.invalidateQueries({ queryKey: ['HOPastOffers'] })
      queryClient.invalidateQueries({ queryKey: ['Notifications'] })
    },
  })

  const createOffer = guardSupportMutation(createOfferMutation)

  return {
    isCreatingOfferLoading,
    isCreatingOfferSuccess,
    isCreatingOfferError,
    createOffer,
  }
}

export function useRemoveOffer() {
  const queryClient = useQueryClient()

  const {
    isPending: isRemovingOfferLoading,
    isSuccess: isRemovingOfferSuccess,
    isError: isRemovingOfferError,
    mutate: removeOfferMutation,
  } = useMutation({
    mutationFn: ({ accommodationId, feedback }: RemoveOfferParams): Promise<AxiosResponse> =>
      apis.hotelOffers.deleteOffer({ accommodationId, feedback }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['HOActiveOffer'] })
      queryClient.invalidateQueries({ queryKey: ['HOPastOffers'] })
    },
  })

  const removeOffer = guardSupportMutation(removeOfferMutation)

  return {
    isRemovingOfferLoading,
    isRemovingOfferSuccess,
    isRemovingOfferError,
    removeOffer,
  }
}

export function useEditOffer() {
  const queryClient = useQueryClient()

  const {
    isPending: isEditingOfferLoading,
    isSuccess: isEditingOfferSuccess,
    isError: isEditingOfferError,
    mutate: editOfferMutation,
  } = useMutation({
    mutationFn: ({ accommodationId, body }: EditOfferParams): Promise<AxiosResponse> =>
      apis.hotelOffers.updateOffer({ accommodationId, body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['HOActiveOffer'] })
      queryClient.invalidateQueries({ queryKey: ['HOPastOffers'] })
    },
  })

  const editOffer = guardSupportMutation(editOfferMutation)

  return {
    isEditingOfferLoading,
    isEditingOfferSuccess,
    isEditingOfferError,
    editOffer,
  }
}
